import { useEffect, useState, useCallback } from "react";
import { BellRinging, BellSlash, CircleNotch, Check, Info } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";
import {
  subscribePush,
  unsubscribePush,
  isSubscribed,
  currentPermission,
  pushSupportStatus,
  sendTestPush,
} from "../../utils/pushClient";

const API = window.location.origin;

/**
 * Bell icon in the header. Two states:
 *  - inactive (grey slash) → click to enable → permission prompt → subscribe
 *  - active (green ringing) → click to see menu with "Test" and "Disable"
 */
export default function PushBell() {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type: 'ok'|'err', text}
  const [support] = useState(() => pushSupportStatus());
  const [diagnostic, setDiagnostic] = useState(null); // full dump for modal

  const refresh = useCallback(async () => {
    setSubscribed(await isSubscribed());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), feedback.type === "err" ? 12000 : 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      setFeedback({ type: "ok", text: "Powiadomienia włączone" });
      await refresh();
    } catch (e) {
      setFeedback({ type: "err", text: e.message || "Błąd" });
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      setFeedback({ type: "ok", text: "Powiadomienia wyłączone" });
      setMenuOpen(false);
      await refresh();
    } catch (e) {
      setFeedback({ type: "err", text: e.message || "Błąd" });
    } finally {
      setBusy(false);
    }
  };

  const testPush = async () => {
    setBusy(true);
    try {
      const res = await sendTestPush();
      const sent = res?.sent ?? 0;
      const total = res?.total_subscriptions ?? 0;
      if (sent > 0) {
        setFeedback({ type: "ok", text: `Wysłano ${sent}/${total} · sprawdź telefon` });
        setMenuOpen(false);
      } else {
        // Show full diagnostic modal for debugging
        setDiagnostic(res);
        setMenuOpen(false);
      }
    } catch (e) {
      setFeedback({ type: "err", text: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const openDiagnostic = async () => {
    setBusy(true);
    try {
      // Gather local + server diagnostic snapshot
      const [statusRes, subs] = await Promise.all([
        axios.get(`${API}/api/push/status`).then((r) => r.data).catch((e) => ({ error: e.message })),
        (async () => {
          try {
            const reg = await navigator.serviceWorker.getRegistration("/");
            if (!reg) return { registered: false };
            const sub = await reg.pushManager.getSubscription();
            return {
              registered: true,
              scope: reg.scope,
              active: !!reg.active,
              subscribed: !!sub,
              endpoint_host: sub ? new URL(sub.endpoint).host : null,
            };
          } catch (e) {
            return { error: e.message };
          }
        })(),
      ]);
      setDiagnostic({
        _local_only: true,
        client: {
          userAgent: navigator.userAgent,
          isIOS: /iphone|ipad|ipod/i.test(navigator.userAgent),
          standalone: window.matchMedia?.("(display-mode: standalone)")?.matches || navigator.standalone === true,
          permission: currentPermission(),
          serviceWorker: subs,
        },
        server_state: statusRes,
      });
      setMenuOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const perm = currentPermission();
  const canEnable = support.ok && perm !== "denied";

  const handleClick = () => {
    if (busy) return;
    if (subscribed) {
      setMenuOpen((v) => !v);
    } else {
      enable();
    }
  };

  const tooltip = subscribed
    ? "Powiadomienia włączone"
    : !support.ok
    ? (support.reason === "ios-need-install"
        ? "iPhone: dodaj do ekranu początkowego"
        : "Push nieobsługiwany przez tę przeglądarkę")
    : perm === "denied"
    ? "Powiadomienia zablokowane w przeglądarce"
    : "Włącz powiadomienia";

  return (
    <div className="relative" data-testid="push-bell-wrapper">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy || (!subscribed && !canEnable)}
        title={tooltip}
        aria-label={tooltip}
        data-testid="push-bell-btn"
        className={`relative w-9 h-9 flex items-center justify-center rounded-md transition-colors ${
          subscribed
            ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
            : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
        } ${!subscribed && !canEnable ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {busy ? (
          <CircleNotch className="w-5 h-5 animate-spin" />
        ) : subscribed ? (
          <BellRinging className="w-5 h-5" weight="fill" />
        ) : (
          <BellSlash className="w-5 h-5" />
        )}
        {subscribed && !busy && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white"
            aria-hidden="true"
          />
        )}
      </button>

      {menuOpen && subscribed && (
        <>
          {/* backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-11 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-50 py-1"
               data-testid="push-menu">
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-100">
              Powiadomienia push
            </div>
            <button
              onClick={testPush}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              data-testid="push-test-btn"
            >
              <BellRinging className="w-4 h-4 text-emerald-600" />
              Wyślij testowe
            </button>
            <button
              onClick={openDiagnostic}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              data-testid="push-diag-btn"
            >
              <Info className="w-4 h-4 text-slate-500" />
              Diagnostyka
            </button>
            <button
              onClick={disable}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-rose-600"
              data-testid="push-disable-btn"
            >
              <BellSlash className="w-4 h-4" />
              Wyłącz na tym urządzeniu
            </button>
          </div>
        </>
      )}

      {feedback && (
        <div
          className={`absolute right-0 top-11 z-50 max-w-[320px] whitespace-normal px-3 py-1.5 rounded-md text-xs shadow-lg border ${
            feedback.type === "ok"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-rose-50 border-rose-200 text-rose-800"
          }`}
          data-testid="push-feedback"
        >
          {feedback.type === "ok" && <Check className="w-3 h-3 inline mr-1" />}
          {feedback.text}
        </div>
      )}

      {diagnostic && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-start sm:items-center justify-center p-3 overflow-auto"
          onClick={() => setDiagnostic(null)}
          data-testid="push-diag-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg border max-w-xl w-full my-4 flex flex-col max-h-[95vh]"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold">Diagnostyka Push</h3>
              </div>
              <button
                onClick={() => setDiagnostic(null)}
                className="text-2xl leading-none px-2 hover:bg-slate-100 rounded"
                data-testid="push-diag-close"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3 text-xs">
              {diagnostic.hint && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
                  <div className="font-semibold mb-1">Podpowiedź:</div>
                  <div>{diagnostic.hint}</div>
                </div>
              )}
              <div>
                <div className="font-semibold mb-1 text-slate-700">Pełny raport:</div>
                <pre className="bg-slate-900 text-emerald-300 p-3 rounded text-[10px] leading-tight overflow-x-auto whitespace-pre-wrap break-all">
{JSON.stringify(diagnostic, null, 2)}
                </pre>
              </div>
              <button
                onClick={() => {
                  const text = JSON.stringify(diagnostic, null, 2);
                  navigator.clipboard?.writeText(text)
                    .then(() => setFeedback({ type: "ok", text: "Skopiowano raport" }))
                    .catch(() => setFeedback({ type: "err", text: "Nie udało się skopiować" }));
                }}
                className="w-full px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded text-slate-700 font-medium"
                data-testid="push-diag-copy"
              >
                Kopiuj raport do schowka
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
